/**
 * Morse — „Wie funktioniert das?“: was die Übung kann und wie sie lernt. (06.10.2026)
 *
 * Martin: „Und die Beschreibung, was die Übung kann, fehlt auch noch.“
 * Eine ruhige Karte gleich unter dem Kopf der Seite: was Morse ist (mit einem kleinen Bild von Punkt und Strich),
 * wie die Koch-Methode lernt, was die vier Reiter und „Start“/„Stopp“ tun, wie viel Zeit sinnvoll ist, was
 * „Hilfe“ und „Kennenlernen“ sind. Die Texte stehen in morse-texte.ts.
 *
 *   - Beim ersten Öffnen ist sie offen; „Verstanden“ schließt sie und merkt das am Gerät (localStorage).
 *   - Der Knopf „Wie funktioniert das?“ im Kopf der Seite öffnet und schließt sie jederzeit (aria-expanded).
 *   - Geöffnet über den Knopf, springt der Fokus auf ihre Überschrift; geschlossen, zurück auf den Knopf.
 */
import { MORSE_TABELLE } from "../../shared/morse.js";
import { musterBild, silbenText } from "./morse-bild.js";
import { EINFUEHRUNG } from "./morse-texte.js";
import { el, knopf } from "./morse-teile.js";

const GELESEN_SCHLUESSEL = "vespera.morse.einfuehrung";

export class MorseEinfuehrung {
  /** Die Karte (unter dem Kopf der Seite). */
  readonly wurzel: HTMLElement;
  /** Der Knopf für den Kopf der Seite. */
  readonly knopf: HTMLButtonElement;
  private readonly titel: HTMLElement;
  private readonly beiZu: (() => void) | undefined;

  /**
   * `hoerspur`: der Platz der Vorführung (morse-hoerspur-ansicht.ts) — gleich unter dem Titel, damit man sie sofort
   * findet. `beiZu`: die Karte wird zugeklappt (dann schweigt, was darin spielt).
   */
  constructor(o: { hoerspur?: HTMLElement; beiZu?: () => void } = {}) {
    this.beiZu = o.beiZu;
    this.wurzel = el("section", "morse-einfuehrung");
    this.wurzel.id = "morse-einfuehrung";
    this.titel = el("h2", "morse-einfuehrung-titel", EINFUEHRUNG.titel);
    this.titel.id = "morse-einfuehrung-titel";
    this.titel.tabIndex = -1;
    this.wurzel.setAttribute("aria-labelledby", this.titel.id);
    this.wurzel.append(this.titel, el("p", "morse-einfuehrung-einleitung", EINFUEHRUNG.einleitung));
    if (o.hoerspur) this.wurzel.appendChild(o.hoerspur);
    // Die Abschnitte: am Telefon untereinander, am Rechner in zwei Spalten (style.css) — keine Textwand.
    const teile = el("div", "morse-einfuehrung-teile");
    this.wurzel.appendChild(teile);
    for (const a of EINFUEHRUNG.abschnitte) {
      const teil = el("div", "morse-einfuehrung-teil");
      teil.appendChild(el("h3", "morse-einfuehrung-zwischen", a.titel));
      if (a.bild === "punkt-strich") teil.appendChild(this.bildPunktStrich());
      if (a.liste) {
        const ul = el("ul", "morse-einfuehrung-liste");
        for (const x of a.liste) {
          const li = el("li");
          li.append(el("strong", "morse-einfuehrung-name", x.name), document.createTextNode(` — ${x.text}`));
          ul.appendChild(li);
        }
        teil.appendChild(ul);
      }
      for (const t of a.absaetze) teil.appendChild(el("p", "morse-einfuehrung-text", t));
      teile.appendChild(teil);
    }
    const aktionen = el("div", "morse-aktionen");
    aktionen.appendChild(knopf(EINFUEHRUNG.schliessen, "morse-knopf morse-knopf--voll", () => this.schliesse(true)));
    this.wurzel.appendChild(aktionen);

    this.knopf = knopf("", "morse-kopf-knopf", () => (this.wurzel.hidden ? this.oeffne(true) : this.schliesse(true)));
    const form = el("span", "morse-kopf-knopf-form", "?");
    form.setAttribute("aria-hidden", "true");
    this.knopf.append(form, el("span", "morse-kopf-knopf-text", EINFUEHRUNG.knopf));
    this.knopf.setAttribute("aria-controls", this.wurzel.id);

    let gelesen = false;
    try {
      gelesen = localStorage.getItem(GELESEN_SCHLUESSEL) === "gelesen";
    } catch {
      /* Privatmodus: dann eben offen */
    }
    if (gelesen) this.schliesse(false);
    else this.oeffne(false);
  }

  get offen(): boolean {
    return !this.wurzel.hidden;
  }

  oeffne(fokus: boolean): void {
    this.wurzel.hidden = false;
    this.knopf.setAttribute("aria-expanded", "true");
    if (fokus) {
      this.titel.focus({ preventScroll: true });
      try {
        this.wurzel.scrollIntoView({ block: "nearest" });
      } catch {
        /* ältere Browser */
      }
    }
  }

  /** Schließen und merken (wer sie einmal geschlossen hat, sieht sie nicht mehr von selbst). */
  schliesse(fokus: boolean): void {
    const warOffen = !this.wurzel.hidden;
    this.wurzel.hidden = true;
    this.knopf.setAttribute("aria-expanded", "false");
    if (!warOffen) return;
    this.beiZu?.();
    try {
      localStorage.setItem(GELESEN_SCHLUESSEL, "gelesen");
    } catch {
      /* Privatmodus */
    }
    if (fokus) this.knopf.focus({ preventScroll: true });
  }

  /** Punkt (eine Einheit) und Strich (drei) als Bild, dazu das A — dasselbe Bild wie überall auf der Seite. */
  private bildPunktStrich(): HTMLElement {
    const f = el("div", "morse-einfuehrung-bild");
    const teil = (muster: string, text: string): HTMLElement => {
      const t = el("p", "morse-einfuehrung-bildteil");
      const b = musterBild(muster, "l");
      b.setAttribute("aria-hidden", "true");
      t.append(b, el("span", "morse-einfuehrung-bildtext", text));
      return t;
    };
    const a = MORSE_TABELLE[EINFUEHRUNG.bildBeispiel] ?? ".-";
    const beispiel = el("p", "morse-einfuehrung-bildteil");
    beispiel.classList.add("morse-einfuehrung-bildteil--beispiel");
    const b = musterBild(a, "l");
    b.setAttribute("aria-hidden", "true");
    beispiel.append(el("span", "morse-einfuehrung-buchstabe", EINFUEHRUNG.bildBeispiel), b, silbenText(a, "morse-einfuehrung-bildtext"));
    f.append(teil(".", EINFUEHRUNG.bildPunkt), teil("-", EINFUEHRUNG.bildStrich), beispiel);
    return f;
  }
}
